// Sender-Fields v1 — amortization math for the pre-approval letter's Computed
// sender fields (monthly payment, balloon payment, balloon due date). Pure
// functions so the composer can recompute live as staff edit the inputs.
// Currency is rounded to cents.

/** Round to cents (2 decimals), avoiding binary-float drift. */
function roundCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Standard amortizing monthly payment. `annualRatePct` is a percent
 *  (e.g. 7.5 for 7.5%), not a fraction. Zero-rate degrades to straight-line. */
export function monthlyPaymentFromAnnualRatePercent(
  principal: number,
  annualRatePct: number,
  amortMonths: number,
): number {
  if (!(principal > 0) || !(amortMonths > 0)) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return roundCents(principal / amortMonths);
  const factor = Math.pow(1 + r, amortMonths);
  return roundCents((principal * r * factor) / (factor - 1));
}

/** Outstanding balance due at the balloon, given the amortizing payment. */
export function balloonPayment(
  principal: number,
  annualRatePct: number,
  payment: number,
  balloonMonths: number,
): number {
  if (!(principal > 0) || !(balloonMonths > 0)) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return roundCents(Math.max(0, principal - payment * balloonMonths));
  const factor = Math.pow(1 + r, balloonMonths);
  const balance = principal * factor - payment * ((factor - 1) / r);
  return roundCents(Math.max(0, balance));
}

/** firstPaymentDate + balloonMonths, returned as an ISO yyyy-MM-dd string.
 *  Returns "" when the input date is missing/invalid. */
export function balloonDueDate(firstPaymentDateIso: string, balloonMonths: number): string {
  if (!firstPaymentDateIso) return "";
  const base = new Date(`${firstPaymentDateIso}T00:00:00Z`);
  if (Number.isNaN(base.getTime())) return "";
  const due = new Date(base);
  due.setUTCMonth(due.getUTCMonth() + Math.round(balloonMonths));
  return due.toISOString().slice(0, 10);
}

/** "20 years" when months divide evenly into years, else "{n} months".
 *  Used for the letter's display Amortization / LoanTerm fields. */
export function monthsToTermLabel(months: number): string {
  if (!(months > 0)) return "";
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${months} month${months === 1 ? "" : "s"}`;
}

export interface LoanFigures {
  monthlyPayment: number;
  balloonPayment: number;
  balloonDueDate: string;
}

/** Compute the three derived loan figures in one call. */
export function computeLoanFigures(input: {
  principal: number;
  annualRatePct: number;
  amortMonths: number;
  balloonMonths: number;
  firstPaymentDateIso: string;
}): LoanFigures {
  const payment = monthlyPaymentFromAnnualRatePercent(
    input.principal, input.annualRatePct, input.amortMonths);
  return {
    monthlyPayment: payment,
    balloonPayment: balloonPayment(
      input.principal, input.annualRatePct, payment, input.balloonMonths),
    balloonDueDate: balloonDueDate(input.firstPaymentDateIso, input.balloonMonths),
  };
}
